import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThinkingPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/posts`);
        setPosts(response.data);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <div data-testid="thinking-page">
      {/* Hero */}
      <section className="py-24 md:py-32" data-testid="thinking-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="accent-bar"></div>
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#1565C0]">Insights</p>
            </div>
            
            <h1 className="font-['Cabinet_Grotesk',sans-serif] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-[#0D1B2A] leading-[1.1] mb-8" data-testid="thinking-heading">
              Thinking
            </h1>
            <p className="text-xl text-[#3D5A73] leading-relaxed">
              Thoughts on business, clarity, and making better decisions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts List */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="py-16 text-center text-[#607D8B]" data-testid="loading-state">
              Loading posts...
            </div>
          ) : posts.length === 0 ? (
            <div className="py-16 text-center text-[#607D8B]" data-testid="empty-state">
              No posts yet.
            </div>
          ) : (
            <div className="border-t-2 border-[#E0E7ED]" data-testid="posts-list">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <Link
                    to={`/thinking/${post.id}`}
                    className="blog-item group block"
                    data-testid={`post-item-${index}`}
                  >
                    <div className="flex items-center justify-between py-8 md:py-10 border-b-2 border-[#E0E7ED]">
                      <div className="flex-1 pr-8">
                        <h2 className="font-['Cabinet_Grotesk',sans-serif] text-xl md:text-2xl font-bold text-[#0D1B2A] group-hover:text-[#1565C0] transition-colors mb-3">
                          {post.title}
                        </h2>
                        <p className="text-[#3D5A73] leading-relaxed line-clamp-2">
                          {post.excerpt}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-[#F0F4F8] group-hover:bg-[#1565C0] flex items-center justify-center transition-all">
                          <ArrowRight 
                            size={18} 
                            weight="bold"
                            className="text-[#607D8B] group-hover:text-white transition-colors" 
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
